import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';
import { Warning as WarningIcon } from '@mui/icons-material';

interface DeleteOrgConfirmationModalProps {
  onClose: () => void;
  onConfirm: () => Promise<void>;
  organizationName: string;
}

export const DeleteOrgConfirmationModal: React.FC<DeleteOrgConfirmationModalProps> = ({
  onClose,
  onConfirm,
  organizationName,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const handleConfirm = async () => {
    if (confirmText !== organizationName) {
      setModalError('Organization name does not match');
      return;
    }

    setModalError(null);
    setIsSubmitting(true);

    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to delete organization');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setModalError(null);
  };

  const isConfirmValid = () => {
    return confirmText === organizationName;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-red-500/50 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <WarningIcon className="text-red-500 w-6 h-6" />
            <h2 className="text-white text-xl font-semibold">Delete Organization</h2>
          </div>
          <button onClick={handleClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        <div className="space-y-4">
          {/* Warning Message */}
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
            <p className="text-red-400 text-sm">
              <strong className="font-semibold">Warning:</strong> This action cannot be undone.
              Deleting this organization will permanently remove:
            </p>
            <ul className="text-red-400 text-sm mt-2 ml-4 list-disc space-y-1">
              <li>All organization data and settings</li>
              <li>All members and their roles</li>
              <li>All pending invitations</li>
              <li>All associated contracts and documents</li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Type <span className="font-mono bg-white/10 px-1 rounded">{organizationName}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={organizationName}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-red-500/50"
            />
          </div>

          {/* Additional Confirmation */}
          <div className="text-white/60 text-sm">
            <p>Are you absolutely sure you want to delete this organization?</p>
            <p className="mt-2">This will affect all members and cannot be recovered.</p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleClose} variant="ghost" className="flex-1">Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="default"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            disabled={!isConfirmValid() || isSubmitting}
          >
            {isSubmitting ? 'Deleting...' : 'Delete Organization'}
          </Button>
        </div>
      </div>
    </div>
  );
};
